import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, message, Typography, Tag, Popconfirm, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const UnitsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchUnits = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/master/units');
            setUnits(data);
        } catch (error) {
            message.error('Failed to fetch units');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUnits();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/master/units/${id}`);
            message.success('Unit deleted successfully');
            fetchUnits();
        } catch (error) {
            message.error('Failed to delete unit');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/units/${editingId}`, values);
                message.success('Unit updated successfully');
            } else {
                await client.post('/master/units', values);
                message.success('Unit created successfully');
            }
            setIsModalVisible(false);
            fetchUnits();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Unit Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Abbreviation',
            dataIndex: 'abbreviation',
            key: 'abbreviation',
            render: (text) => <Tag color="blue" style={{ borderRadius: 12, padding: '0 12px' }}>{text}</Tag>
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Unit"
                        description="Are you sure you want to delete this unit?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Units of Measure</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage units for inventory, sales, and purchasing.</Typography.Text>
                </div>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleAdd}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                    >
                        Add Unit
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} md={8}>
                    <StatCard
                        title="Registered Units"
                        value={units.length}
                        icon={<i className="fa-solid fa-scale-balanced"></i>}
                        color="#3b82f6"
                        subText="Standard measurement units"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={units}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 800 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Unit' : 'Create New Unit of Measure'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={400}
                className="premium-modal"
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item name="name" label="Unit Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="e.g. Kilograms" />
                    </Form.Item>
                    <Form.Item name="abbreviation" label="Abbreviation" rules={[{ required: true, message: 'Abbreviation is required' }]}>
                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="e.g. kg" />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Create Unit'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default UnitsPage;




